import React, { FC } from "react";
import { Button, Col, DatePicker, Form, Input, InputNumber, Row, Upload } from "antd";
import { CheckCircleOutlined, EyeOutlined, UploadOutlined } from "@ant-design/icons";
import { useUserFunctionalities } from "../../../../utils/UserFunctionalities";

type PreAuthHealthIndiaProps = {
  form: any;
  handleSaveConfirmationModal: (
    state: "save" | "update" | "intimate" | "approve" | "initiate" | "submit"
  ) => void;
  handleSubmit?: () => void;
  isSaving: boolean;
  isIntimating: boolean;
  isApproving: boolean;
  isSubmitting?: boolean;
  integratedWithPortal?: boolean;
  isApproved: boolean;
  isSubmitted: boolean;
  isInitiated: boolean;
  isIntimated: boolean;
  hasExistingRecord: boolean;
  documentRequestResponseDocuments: any;
  documentUrls: any[];
  handleFileChange: (documentType: string, isMultiple: boolean) => (e: any) => void;
  handleOpen: (docType: any, documentUrl: any) => void;
  uploadFileList: any[];
  showVerifyButton?: boolean;
  isValidationLoading?: boolean;
  registrationValidationStatus?: any;
  handleClaimValidation?: () => void;
};

const PreAuthHealthIndia: FC<PreAuthHealthIndiaProps> = ({
  form,
  handleSaveConfirmationModal,
  handleSubmit,
  isSaving,
  isIntimating,
  isApproving,
  isSubmitting = false,
  integratedWithPortal = true,
  isApproved,
  isSubmitted,
  isInitiated,
  isIntimated,
  hasExistingRecord,
  documentRequestResponseDocuments,
  documentUrls,
  handleFileChange,
  handleOpen,
  uploadFileList,
  showVerifyButton,
  isValidationLoading,
  registrationValidationStatus,
  handleClaimValidation,
}) => {
  const { hasFunctionality } = useUserFunctionalities();
  const actionButtonStyle = {
    backgroundColor: "rgb(0, 76, 143)",
    color: "#fff",
    boxShadow: "0px 2px 8px 0px #00000040",
    width: "100%",
  };

  const renderDocumentRows = (type: "requestedDocuments" | "responseDocuments") => {
    const docs = documentRequestResponseDocuments?.[type] || [];
    return docs?.map((docType: any) => {
      const matchingDocuments = documentUrls?.filter(
        (doc) => doc?.documentLabelType === docType?.label
      );

      return (
        <div
          key={`${type}-${docType?.id}`}
          style={{
            border: "1px solid #eee",
            borderRadius: 8,
            padding: "8px 10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Upload
              multiple={Boolean(docType?.multiple)}
              showUploadList={false}
              beforeUpload={() => false}
              {...(docType?.multiple ? { fileList: uploadFileList } : {})}
              onChange={handleFileChange(docType?.labelId, Boolean(docType?.multiple))}
              disabled={isSubmitted}
            >
              <Button icon={<UploadOutlined />} disabled={isSubmitted} />
            </Upload>
            <span>
              {docType?.label}
              {docType?.required ? <span style={{ color: "red" }}> *</span> : null}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {matchingDocuments?.map((doc: any, idx: number) => (
              <Button
                key={`${doc?.id}-${idx}`}
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleOpen(doc?.documentLabelType, doc?.documentUrl)}
              >
                {idx + 1}
              </Button>
            ))}
          </div>
        </div>
      );
    });
  };

  return (
    <div style={{ margin: "0px 0.5rem" }}>
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Admission date"
              name="admissionDate"
              rules={[{ required: true, message: "Please select admission date" }]}
            >
              <DatePicker format="DD-MM-YYYY HH:mm:ss" style={{ width: "100%" }} disabled={isSubmitted}/>
            </Form.Item>
          </Col>
          {/* <Col span={8}>
            <Form.Item label="Discharge date" name="dischargeDate">
              <DatePicker format="DD-MM-YYYY HH:mm:ss" style={{ width: "100%" }} />
            </Form.Item>
          </Col> */}
          <Col span={8}>
            <Form.Item
              label="Stay In Hospital (In Days)"
              name="stayInHospitalDays"
              rules={[
                { required: true, message: "Please enter stay in days" },
                {
                  pattern: /^\d{1,3}$/,
                  message: "Days should be upto 3 digits only",
                },
              ]}
            >
              <InputNumber min={1} style={{ width: "100%" }} disabled={isSubmitted}/>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Diagnosis/Ailment"
              name="diagnosisAilment"
              rules={[{ required: true, message: "Please enter diagnosis/ailment" }]}
            >
              <Input.TextArea rows={3} placeholder="Please provide detailed summary that your hospital provided" disabled={isSubmitted} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Claimed Amount"
              name="claimedAmount"
              rules={[{ required: true, message: "Please enter claimed amount" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} placeholder="Claimed Amount" disabled={isSubmitted} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Treating Doctor Name"
              name="treatingDoctorName"
              rules={[{ required: true, message: "Please enter treating doctor name" }]}
            >
              <Input disabled={isSubmitted}/>
            </Form.Item>
          </Col>
        </Row>

        <h4 style={{ marginTop: 12 }}>Request Document Upload</h4>
        {renderDocumentRows("requestedDocuments")}

        <h4 style={{ marginTop: 12 }}>Response Document Upload</h4>
        {renderDocumentRows("responseDocuments")}

        <div
          style={{
            marginTop: 20,
            borderTop: "1px solid #D9D9D9",
            padding: "15px 20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "30px",
          }}
        >
          {!isSubmitted ? (
            <>
              {hasExistingRecord && !isInitiated ? (
                <div style={{ width: "16%" }}>
                  <Button
                    type="primary"
                    onClick={() => handleSaveConfirmationModal("update")}
                    loading={isSaving}
                    disabled={isSubmitted}
                    style={actionButtonStyle}
                  >
                    UPDATE
                  </Button>
                </div>
              ) : (
                !hasExistingRecord && (
                  <div style={{ width: "16%" }}>
                    <Button
                      type="primary"
                      onClick={() => handleSaveConfirmationModal("save")}
                      loading={isSaving}
                      disabled={isSubmitted}
                      style={actionButtonStyle}
                    >
                      SAVE
                    </Button>
                  </div>
                )
              )}

              {hasFunctionality("casePreAuthIntimate") &&
                !isIntimated &&
                !isInitiated &&
                !isApproved && (
                  <div style={{ width: "16%" }}>
                    <Button
                      onClick={() => handleSaveConfirmationModal("intimate")}
                      loading={isIntimating}
                      disabled={isSubmitted}
                      style={actionButtonStyle}
                    >
                      Send To QC
                    </Button>
                  </div>
                )}

              {hasFunctionality("preAuthApprover") &&
                !isInitiated &&
                !isApproved &&
                !isSubmitted && (
                  <div style={{ width: "16%" }}>
                    <Button
                      onClick={() => handleSaveConfirmationModal("approve")}
                      loading={isApproving}
                      disabled={isSubmitted || isApproved}
                      style={actionButtonStyle}
                    >
                      QC Approve
                    </Button>
                  </div>
                )}

              {hasFunctionality("casePreAuthSubmission") && !isInitiated && !isSubmitted && (
                <div style={{ width: "16%" }}>
                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={isSubmitted}
                    style={actionButtonStyle}
                  >
                    {integratedWithPortal ? "Initiate" : "Submit Manually"}
                  </Button>
                </div>
              )}
            </>
          ) : null}
        </div>
      </Form>

      {isSubmitted && showVerifyButton && handleClaimValidation ? (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={isValidationLoading}
            onClick={handleClaimValidation}
            style={{
              backgroundColor: "rgb(0, 76, 143)",
              color: "#fff",
              boxShadow: "0px 2px 8px 0px #00000040",
              minWidth: 140,
            }}
          >
            {registrationValidationStatus ? "View" : "Verify"}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(PreAuthHealthIndia);
